/**
 * Authorization Error Class
 *
 * Why: Specialized handling for permission failures
 * What: Error class for 403 forbidden responses
 *
 * Benefits:
 *      - Clear distinction from authentication errors
 *      - Permission-specific messaging
 *      - No unnecessary logout triggers
 *
 * Use Cases:
 *      - User lacks required role
 *      - Resource access denied
 *      - Feature not available in plan
 */

import { AppError, type AppErrorOptions } from "./base/AppError";
import { ErrorCategory, ErrorSeverity } from "../types";
import { ERROR_CODES, ERROR_MESSAGES } from "../constants";

/***
 * Options specific to authorization errors
 */
export interface AuthorizationErrorOptions extends Omit<
  AppErrorOptions,
  "statusCode"
> {
  /** Required permission that was missing */
  requiredPermission?: string;
  /** Resource that was being accessed */
  resource?: string;
}

/**
 * Authorization Error
 *
 * Represents permission/access failures.
 * User is authenticated but lacks permission.
 */
export class AuthorizationError extends AppError {
  /** THe permission that was required */
  public readonly requiredPermission?: string;

  /** The resource being accessed */
  public readonly resource?: string;

  constructor(
    message: string = ERROR_MESSAGES.FORBIDDEN,
    options: AuthorizationErrorOptions = {},
  ) {
    super(message, {
      statusCode: 403,
      code: options.code ?? ERROR_CODES.AUTHZ_FORBIDDEN,
      category: ErrorCategory.AUTHORIZATION,
      severity: options.severity ?? ErrorSeverity.MEDIUM,
      isOperational: true,
      isRetryable: false,
      context: options.context,
      cause: options.cause,
    });
    this.requiredPermission = options.requiredPermission;
    this.resource = options.resource;
  }

  /**
   * Create error for insufficient permission
   */
  static insufficientPermission(
    requiredPermission: string,
    cause?: unknown,
  ): AuthorizationError {
    return new AuthorizationError(ERROR_MESSAGES.INSUFFICIENT_PERMISSIONS, {
      code: ERROR_CODES.AUTHZ_INSUFFICIENT_PERMISSIONS,
      requiredPermission,
      cause,
    });
  }

  /**
   * Create error for resource access denied
   */
  static accessDenied(resource: string, cause?: unknown): AuthorizationError {
    return new AuthorizationError(ERROR_MESSAGES.ACCESS_DENIED, {
      code: ERROR_CODES.AUTHZ_ACCESS_DENIED,
      resource,
      cause,
    });
  }
}
